import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { KeyRound, ShieldCheck, ArrowLeft, Send } from "lucide-react";

export default function AdminLogin() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [stage, setStage] = useState("login"); // login | code
  const [form, setForm] = useState({ email: "", password: "" });
  const [tempToken, setTempToken] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const submitLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", form);
      setTempToken(data.temp_token);
      setResendIn(data.resend_available_in || 60);
      toast.success(data.telegram_delivered ? "Код отправлен в Telegram" : "Проверьте логи сервера (TG недоступен)");
      setStage("code");
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const verify = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify", { temp_token: tempToken, code });
      setUser(data.user);
      toast.success("Добро пожаловать, Артём Альбертович!");
      nav("/admin/dashboard");
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const resend = async () => {
    try {
      const { data } = await api.post("/auth/resend", { temp_token: tempToken });
      setResendIn(data.resend_available_in || 60);
      toast.success(data.telegram_delivered ? "Код отправлен повторно" : "Проверьте логи сервера");
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen grid-noise flex items-center justify-center p-4">
      <button onClick={() => nav("/")} className="fixed top-6 left-6 btn-outline !py-2 !px-4 text-xs inline-flex items-center gap-2" data-testid="admin-back-home">
        <ArrowLeft size={14}/> Home
      </button>
      <div className="w-full max-w-md glass rounded-3xl p-8 sm:p-10 fade-up" data-testid="admin-login-card">
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto rounded-2xl glass-cyan flex items-center justify-center mb-4">
            {stage === "login" ? <KeyRound className="text-cyan"/> : <ShieldCheck className="text-cyan"/>}
          </div>
          <div className="chip mb-3">ARVEX ADMIN</div>
          <h1 className="font-heading text-3xl font-bold">
            {stage === "login" ? "Вход в панель" : "Код из Telegram"}
          </h1>
          {stage === "code" && <p className="text-gray-400 text-sm mt-2">Введите 6-значный код, отправленный в Telegram. Код действителен 5 минут.</p>}
        </div>

        {stage === "login" ? (
          <form onSubmit={submitLogin} className="space-y-5" data-testid="admin-login-form">
            <div>
              <label>Email</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="mt-2" data-testid="admin-username-input"/>
            </div>
            <div>
              <label>Пароль</label>
              <input required type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} className="mt-2" data-testid="admin-password-input"/>
            </div>
            <button type="submit" disabled={loading} className="btn-gold w-full inline-flex items-center justify-center gap-2" data-testid="admin-login-submit">
              {loading ? "…" : "Войти"} <Send size={14}/>
            </button>
          </form>
        ) : (
          <div className="space-y-5">
            <input placeholder="123456" maxLength={6} value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="text-center font-mono text-2xl tracking-widest" data-testid="twofa-code-input"/>
            <button onClick={verify} disabled={loading || code.length !== 6} className="btn-gold w-full" data-testid="twofa-verify-submit">
              {loading ? "…" : "Подтвердить"}
            </button>
            <button onClick={resend} disabled={resendIn > 0} className="btn-outline w-full text-sm" data-testid="twofa-resend">
              {resendIn > 0 ? `📩 Отправить повторно (${resendIn}с)` : "📩 Отправить повторно"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
